import { useMemo } from 'react';
import { Box2, Shape, Vector2 } from 'three';

import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';

import { useLayers } from '../../hooks/index.js';
import { FPolygonNode } from '../../types/index.js';
import { FComposition, FCompositionProps } from '../FComposition/index.js';

export type FPolygonProps = GraphNodeProps<FPolygonNode> & FCompositionProps;

export const FPolygon: React.FC<FPolygonProps> = ({ points, attrs, ...props }) => {
  const layers = useLayers();
  const finalPoints = useMemo(() => (points[0] ? [...points, points[0]] : []), [points]);

  const { size } = useMemo(() => {
    const box = new Box2().setFromPoints(points.map((p) => new Vector2(p[0], p[1])));
    return {
      size: box.getSize(new Vector2()).toArray(),
      position: box.getCenter(new Vector2()).toArray(),
    };
  }, [points]);

  const mask = useMemo(() => {
    if (!finalPoints.length) {
      return [];
    }

    const mapFn = (v: Vector2) => v.toArray();
    const shape = new Shape(finalPoints.map((p) => new Vector2().fromArray(p)));
    const points = shape.getPoints().map(mapFn);
    const holePoints = shape.getPointsHoles(12).map((v) => v.map(mapFn));
    const polygon = [points, ...holePoints];

    return polygon;
  }, [finalPoints]);

  return (
    <>
      <fpolygon points={points}></fpolygon>
      <FComposition
        {...{ ...props }}
        attrs={{
          ...attrs,
          mask,
          size: attrs?.size ?? size,
        }}
        visible={props.visible !== false && layers.isEnabled(FShallowNodeTypesEnum.FPolygon)}
      />
    </>
  );
};
